import React, { useState, useEffect, useCallback } from "react";
import { FaSearch, FaChevronRight, FaSort, FaDollarSign, FaMapMarkerAlt, FaCalendarAlt, FaClock, FaTrophy, FaMoneyBillWave } from "react-icons/fa";
import { FaSatelliteDish } from "react-icons/fa6";
import Select from "react-select";
import { ThreeDots } from 'react-loader-spinner';
import axiosInstance from "../api/axiosInstance";
import API_ENDPOINTS from "../api/endpoints";
import { toast } from 'react-toastify';
import SessionModal from "../components/Modal/SessionModal";
import Modal from '../components/Modal/Modal'; // or wherever your Modal is
import { useNavigate } from "react-router-dom";

const customStyles = {
    control: (provided) => ({
        ...provided,
        backgroundColor: "transparent",
        borderColor: "#292524",
        color: "white",
        padding: "0.1rem 0.25rem",
    }),
    singleValue: (provided) => ({
        ...provided,
        color: "white",
    }),
    menu: (provided) => ({
        ...provided,
        backgroundColor: "#111",
    }),
    option: (provided, state) => ({
        ...provided,
        backgroundColor: state.isFocused ? "#1f2937" : "#111",
        color: "white",
    }),
    placeholder: (provided) => ({
        ...provided,
        color: "white",
    }),
    input: (provided) => ({
        ...provided,
        color: "white",
    }),
};


function getSessionIcon(type) {
    if (type === "Tournament") return <FaTrophy className="text-blue-400" />;
    if (type === "Cash") return <FaMoneyBillWave className="text-green-400" />;
    if (type === "Satellite") return <FaSatelliteDish className="text-gray-400" />;
    return <FaTrophy className="text-blue-400" />;
}

function formatCurrency(val) {
    const num = Number(val);
    return (num < 0 ? "-$" : "+$") + Math.abs(num).toLocaleString(undefined, { minimumFractionDigits: 2 });
}

function formatDate(dateStr) {
    // Expects YYYY-MM-DD
    const d = new Date(dateStr);
    return d.toLocaleDateString(undefined, { month: "short", day: "2-digit", year: "numeric" });
}

function formatTime(timeStr) {
    // Expects "YYYY-MM-DD HH:mm:ss"
    if (!timeStr) return "";
    const d = new Date(timeStr.replace(" ", "T"));
    return d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
}

export default function Sessions() {
    const navigate = useNavigate();
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState("");
    const [type, setType] = useState(""); // type is value (string)
    const [sortAsc, setSortAsc] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [sessionData, setSessionData] = useState({
        gameName: "",
        gameType: "",
        gameLocation: "",
        buyIn: 0,
        gameDate: "",
        gameStart: "",
        pl: 0,
        tsPosition: null,
        tsCount: null,
    });
    const [variantOptions, setVariantOptions] = useState([]);
    const [locationOptions, setLocationOptions] = useState([]);

    // Location modal state
    const [locationModalOpen, setLocationModalOpen] = useState(false);
    const [modeLocation, setModeLocation] = useState("create");
    const [locationData, setLocationData] = useState({
        id: "",
        name: "",
        type: "",
        address: "",
        sessions: 0,
    });
    const [gameTypes, setGameTypes] = useState([]);
    const [locationTypes, setLocationTypes] = useState([]);

    // Fetch game types once on mount
    useEffect(() => {
        const fetchGameTypes = async () => {
            try {
                const response = await axiosInstance.get(`${API_ENDPOINTS.SELECTSCHEMA}`);
                // response.data.gameTypes should be [{ label, value }]
                setGameTypes(response.data.gameTypes || []);
                setVariantOptions(response.data.gameVarients || []);
            } catch (error) {
                toast.error(error.response?.data?.message || "Failed to fetch game types");
            }
        };
        fetchGameTypes();
    }, []);

    // Fetch locations function (move outside useEffect)
    const fetchLocations = useCallback(async () => {
        try {
            const response = await axiosInstance.get(API_ENDPOINTS.LOCATIONS);
            setLocationOptions(
                (response.data.data || []).map(loc => ({
                    label: loc.name,
                    value: loc.name, // or loc.id if you want to use IDs
                }))
            );
        } catch (error) {
            toast.error(error.response?.data?.message || "Failed to fetch locations");
        }
    }, []);

    // Fetch locations on mount
    useEffect(() => {
        fetchLocations();
    }, [fetchLocations]);

    // Debounced search and fetch sessions
    const fetchData = useCallback(async (searchItem) => {
        setLoading(true);
        try {
            const response = await axiosInstance.get(`${API_ENDPOINTS.SESSIONS}?q=${searchItem}`);
            setData(response.data.data);
        } catch (error) {
            toast.error(error.response?.data?.message || "Something went wrong");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        const handler = setTimeout(() => {
            fetchData(search);
        }, 400); // 400ms debounce
        return () => clearTimeout(handler);
    }, [fetchData, search]);

    // Filter and sort sessions by selected type
    let sessions = data
        .filter(s => !type || s.gameType === type)
        .sort((a, b) =>
            sortAsc
                ? new Date(a.gameDate) - new Date(b.gameDate)
                : new Date(b.gameDate) - new Date(a.gameDate)
        );

    const handleSave = async () => {
        setLoading(true);
        try {

            // Create new session
            await axiosInstance.post(API_ENDPOINTS.SESSIONS, sessionData);
            toast.success("Session created successfully");

            setModalOpen(false);
            fetchData(search); // Refresh data after save
            setSessionData({
                gameName: "",
                gameType: "",
                gameLocation: "",
                buyIn: 0,
                gameDate: "",
                gameStart: "",
                pl: 0,
                tsPosition: null,
                tsCount: null,
            });
        } catch (error) {
            toast.error(error.response?.data?.message || "Failed to save session");
        } finally {
            setLoading(false);
        }
    }

    const handleChange = (e) => {
        const { name, value } = e.target;
        setSessionData((prev) => ({
            ...prev,
            [name]: value,
        }));
    }

    // Fetch game types for location modal
    useEffect(() => {
        const fetchLocationTypes = async () => {
            try {
                const response = await axiosInstance.get(`${API_ENDPOINTS.SELECTSCHEMA}`);
                setLocationTypes(response.data.locationTypes);
            } catch (error) {
                toast.error(error.response?.data?.message || "Failed to fetch game types");
            }
        };
        fetchLocationTypes();
    }, []);

    const handleOpenLocationModal = () => {
        setLocationData({ name: "", type: "", address: "", sessions: 0 });
        setModeLocation("create");
        setLocationModalOpen(true);
    };

    const handleCancelLocation = () => {
        setLocationModalOpen(false);
        setLocationData({ name: "", type: "", address: "", sessions: 0 });
    };

    const handleChangeLocation = (e) => {
        const { name, value } = e.target;
        setLocationData((prev) => ({ ...prev, [name]: value }));
    };

    const handleSaveLocation = async () => {
        const formData = {
            name: locationData.name,
            type: locationData.type,
            address: locationData.address,
        };
        setLoading(true);
        try {
            await axiosInstance.post(`${API_ENDPOINTS.LOCATION}`, formData);
            toast.success("Location created successfully");
            setLocationModalOpen(false);
            fetchLocations(); // <-- Fetch locations again after creating
        } catch (error) {
            toast.error(error.response?.data?.error || "Something went wrong");
        }finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-black text-white px-6 py-6">
            {loading &&
                <ThreeDots
                    visible={true}
                    height="80"
                    width="80"
                    color="#1976d2"
                    radius="9"
                    ariaLabel="three-dots-loading"
                    wrapperStyle={{}}
                    wrapperClass="dots-loading additional-class"
                />
            }
            <div className="flex justify-start md:justify-between flex-col md:flex-row md:items-center mb-6">
                <h1 className="text-3xl font-bold">Sessions</h1>
                <button className="bg-secondary mt-5 md:mt-0 justify-center hover:opacity-75 px-4 py-2 rounded text-white flex items-center gap-2 "
                    onClick={() => setModalOpen(true)}>
                    <span className="text-xl">+</span> Add Session
                </button>
            </div>

            <div className="flex gap-3 mb-6 md:flex-nowrap flex-wrap">
                <div className="relative flex-2 md:flex-1 w-full">
                    <FaSearch className="absolute left-3 top-[50%] -translate-y-1/2 text-gray-400" />
                    <input
                        type="text"
                        className="w-full bg-transparent border border-border rounded pl-10 pr-3 py-2 text-white placeholder-gray-400"
                        placeholder="Search sessions..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                </div>
                <Select
                    styles={customStyles}
                    options={[
                        { label: "All Types", value: "" },
                        ...gameTypes.map((t) => ({ label: t.label, value: t.value }))]
                    }
                    className="md:w-[160px] w-[69%]"
                    value={gameTypes.find((t) => t.value === type)}
                    onChange={(option) => setType(option.value)}
                    placeholder="Select Type"
                    classNamePrefix="react-select"
                    isSearchable={false}
                />
                <button
                    className="bg-transparent border max-w-[30%] md:max-w-auto border-border rounded px-4 py-2 flex items-center gap-2 text-white"
                    onClick={() => setSortAsc((s) => !s)}
                >
                    <FaSort />
                    Sort
                </button>
            </div>

            <div className="flex flex-col gap-6">
                {sessions.map((s) => (
                    <div
                        key={s.id}
                        className="bg-transparent border border-border rounded px-6 py-5 flex flex-wrap  items-end  md:items-end justify-between md:justify-start hover:bg-[#111] cursor-pointer"
                        onClick={() => navigate(`/Sessions/SessionCard/${s.id}`)}
                    >
                        <div className="flex-1 md:flex-auto flex flex-col gap-2">
                            <div className="flex items-center gap-2 md:w-[370px]">
                                {getSessionIcon(s.gameType)}
                                <span className="text-lg font-bold">
                                    {s.gameName}
                                </span>
                            </div>
                            <div className="flex items-center gap-3 text-gray-300 text-sm">
                                <FaMapMarkerAlt className="inline mr-1" />
                                {s.gameLocation}
                            </div>
                            <div className="flex items-center gap-3 text-gray-300 text-sm">
                                <FaDollarSign className="inline mr-1" />
                                Buy In: ${Number(s.buyIn).toLocaleString()}
                            </div>
                        </div>
                        <div className="flex-1 md:flex-auto">
                            <div className="flex items-strat md:flex-auto  flex-col gap-4 text-gray-300 text-sm">
                                <div>
                                    <FaCalendarAlt className="inline mr-1" />
                                    {formatDate(s.gameDate)}
                                </div>
                                <div>
                                    <FaClock className="inline  mr-1" />
                                    {formatTime(s.gameStart)}
                                </div>
                            </div>
                        </div>
                        <div className="flex flex-col md:items-end gap-2 mt-4 md:mt-0 w-full md:w-auto md:min-w-[200px]">
                            <div className="flex md:flex-col items-center justify-end md:justify-start  md:items-end ">
                                <span
                                    className={`text-md font-bold ${Number(s.pl) < 0 ? "text-red-500" : "text-green-500"
                                        }`}
                                >
                                    {formatCurrency(s.pl)}
                                </span>
                                {s.tsPosition && s.tsCount &&
                                    <span className="text-gray-400 text-sm">
                                        {s.tsPosition} of {s.tsCount}
                                    </span>
                                }
                                <FaChevronRight className="ml-2 text-gray-400 md:mt-6" />
                            </div>
                        </div>
                    </div>
                ))}
            </div>
            <SessionModal
                isOpen={modalOpen}
                onClose={() => setModalOpen(false)}
                onSave={handleSave}
                mode="create" // or "edit"
                data={sessionData}
                onChange={handleChange}
                variantOptions={variantOptions}
                typeOptions={gameTypes}
                locationOptions={locationOptions}
                onOpenLocationModal={handleOpenLocationModal} // <-- pass handler
            />
            <Modal
                isOpen={locationModalOpen}
                onClose={handleCancelLocation}
                data={locationData}
                onChange={handleChangeLocation}
                onSave={handleSaveLocation}
                mode={modeLocation}
                title={'Add Location'}
                typeOptions={locationTypes}
            />
        </div>
    );
}
